import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  StoryboardView,
  type Command,
  type CreateOutputInput,
  type DialogueLine,
  type GenerationJob,
  type GenerationKind,
  type LibraryKind,
  type Me,
  type OutputLine,
  type PatchOutputInput,
  type RevertOutput,
} from "@dramapie/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { EstimateDialog } from "../components/EstimateDialog";
import { GenerationControls } from "../components/GenerationControls";
import { GenerationQueue } from "../components/GenerationQueue";
import { ToastStack, useToasts } from "../components/Toast";
import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { Storyboard, type StoryboardEditing } from "../components/Storyboard";
import type { LibraryChipTarget } from "../components/ShotCard";
import { AccountMenu } from "../components/workspace/AccountMenu";
import { DataView } from "../components/views/DataView";
import { OutlineView } from "../components/views/OutlineView";
import { ScriptView } from "../components/views/ScriptView";
import { ExportStatus } from "../components/workspace/ExportStatus";
import { CanvasBoard } from "../components/workspace/CanvasBoard";
import { Inspector, type InspectorTab } from "../components/workspace/Inspector";
import { LibraryDialog, type LibraryFocusTarget } from "../components/workspace/LibraryDialog";
import { ProjectSwitcher } from "../components/workspace/ProjectSwitcher";
import { FrameConfirmDialog } from "../components/workspace/FrameConfirmDialog";
import { ShortcutsSheet } from "../components/workspace/ShortcutsSheet";
import { ShareDialog } from "../components/workspace/ShareDialog";
import { TimelineView } from "../components/workspace/TimelineView";
import { UsageDialog } from "../components/workspace/UsageDialog";
import { OutputDialog, type OutputDialogMode } from "../components/workspace/OutputDialog";
import { Rail } from "../components/workspace/Rail";
import { ShotStrip } from "../components/workspace/ShotStrip";
import { Topbar } from "../components/workspace/Topbar";
import {
  ApiError,
  cancelGeneration,
  conflictViewFrom,
  createExport,
  createGeneration,
  createOutput,
  deleteOutput,
  estimateGeneration,
  getCanvas,
  getExport,
  getGeneration,
  getMe,
  getProject,
  getProjects,
  getLibrary,
  getStoryboard,
  getTimeline,
  listGenerations,
  listOutputs,
  listProjectHistory,
  logout,
  moveEpisode,
  patchOutput,
  postCommands,
  undoProject,
} from "../lib/api";
import { summarizeAccount } from "../lib/account";
import { errorMessage } from "../lib/errors";
import { formatClock, formatDateTime, formatPoints } from "../lib/format";
import {
  GENERATION_KIND_META,
  isTerminalJob,
  jobFromEvent,
  jobIdFromEvent,
  replaceJobs,
  upsertJob,
  type PendingGeneration,
} from "../lib/generations";
import { pollExportJob } from "../lib/exports";
import {
  EXPORT_FORBIDDEN_MESSAGE,
  hasOwnCommit,
  isEditableTarget,
  printViewPath,
  type ExportUiState,
} from "../lib/m4";
import {
  countEpisodesByOutput,
  episodesOfOutput,
  outputDeleteBlockedMessage,
  resolveCurrentOutput,
} from "../lib/outputs";
import type { LibraryTab } from "../lib/library-tabs";
import { shortId } from "../lib/status";
import {
  parseViewParam,
  tabForKey,
  viewParamValue,
  type WorkspaceContentView,
  type WorkspaceTab,
} from "../lib/workspace-tabs";
import { applyCommandsLocally, applyProjectEvent } from "../lib/storyboard-commands";
import {
  ProjectStream,
  StreamHub,
  type StreamMessage,
  type StreamStatus,
} from "../lib/stream";
import {
  countByStatus,
  findScene,
  FILTER_LABEL,
  matchesFilter,
  STYLE_LABEL,
  type ShotFilter,
} from "../lib/view";

const MIN_DURATION = 0.5;
const MAX_DURATION = 15;

const FILTERS: ShotFilter[] = ["all", "pending", "review", "approved", "error"];

function sameDialogue(a: DialogueLine[], b: DialogueLine[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((line, index) => {
    const other = b[index];
    return (
      other !== undefined &&
      line.who === other.who &&
      line.type === other.type &&
      line.text === other.text
    );
  });
}

export function WorkspacePage() {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const episodeParam = searchParams.get("ep") ?? undefined;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /**
   * M6 + G-06：视图进 URL（`?view=canvas|timeline|outline|script`），
   * 故事板是默认值不占参数。这样大纲 / 剧本由别的 Agent 接进来时，
   * 直接按 `viewMode` 渲染即可，深链和刷新都还原同一个视图。
   */
  const viewMode = parseViewParam(searchParams.get("view"));

  /**
   * 故事板投影先声明：`?ep=` 缺失时它还能从 WS 快照里补出当前集 id，
   * 不让「项目详情慢 / 单请求挂住」把 storyboard / canvas / timeline 一起锁死。
   */
  const [view, setView] = useState<StoryboardView | null>(null);

  /** 作品切换用：全部项目（G-01）。和项目详情分开取，切换浮层不跟着详情卡。 */
  const projectsQuery = useQuery({
    queryKey: ["projects"],
    queryFn: () => getProjects(),
    staleTime: 30_000,
  });

  const projectQuery = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProject(projectId),
    enabled: projectId.length > 0,
  });
  const project = projectQuery.data;

  /**
   * M8 产出线：左栏那一行 + 浮层用的全量（含 hidden / archived）。
   * 当前产出线进 URL（`?out=`）：刷新 / 深链 / 分享都能还原到同一条线。
   */
  const outputsQuery = useQuery({
    queryKey: ["outputs", projectId],
    queryFn: () => listOutputs(projectId),
    enabled: projectId.length > 0,
  });
  const outputs = outputsQuery.data?.items ?? [];
  const outputParam = searchParams.get("out") ?? undefined;

  /**
   * 当前产出线 id。三种来源按优先级：
   *   1. `?ep=` 指向的那一集归属哪条线（深链定位到集时，产出线跟着集走）；
   *   2. `?out=`（全量还没回来时先用项目详情里的 id 顶上，避免闪空）；
   *   3. 第一条未归档的线。
   */
  const currentOutputId = useMemo<string | null>(() => {
    const fromEpisode = episodeParam
      ? project?.episodes.find((item) => item.id === episodeParam)
      : undefined;
    if (fromEpisode) return fromEpisode.outputId;
    if (outputs.length > 0) return resolveCurrentOutput(outputs, outputParam)?.id ?? null;
    const shallow = project?.outputs.find((item) => item.id === outputParam) ?? project?.outputs[0];
    return shallow?.id ?? null;
  }, [episodeParam, project?.episodes, project?.outputs, outputs, outputParam]);

  /** 面包屑 / 左栏标题：全量优先，项目详情兜底（两者 id 一定对得上）。 */
  const currentOutputTitle =
    outputs.find((item) => item.id === currentOutputId)?.title ??
    project?.outputs.find((item) => item.id === currentOutputId)?.title ??
    null;
  const outputEpisodes = episodesOfOutput(project?.episodes ?? [], currentOutputId);
  const episodeCounts = countEpisodesByOutput(project?.episodes ?? []);

  /**
   * 首次进入就必须带集：`?ep=` 优先，其次**当前产出线**的第一集，
   * 最后（产出线还没解析出来时）项目第一集 / WS 快照里的当前集。
   * 后端在缺参数时会 400（"storyboard 视图必须指定 episode"），
   * 前端不能等用户手动切集才把画面对上；也不能只依赖 /api/projects/:id。
   */
  const resolvedEpisodeId =
    episodeParam ??
    outputEpisodes[0]?.id ??
    (currentOutputId === null ? project?.episodes[0]?.id : undefined) ??
    view?.episode.id;
  const noEpisodes = projectQuery.isSuccess && (project?.episodes.length ?? 0) === 0;
  /** 这个项目有集，但当前产出线一条都没有（契约 §3：新建产出线不自动建集）。 */
  const outputEmpty =
    projectQuery.isSuccess &&
    !noEpisodes &&
    episodeParam === undefined &&
    currentOutputId !== null &&
    outputs.length > 0 &&
    outputEpisodes.length === 0;

  const storyboardQuery = useQuery({
    queryKey: ["storyboard", projectId, resolvedEpisodeId ?? "pending"],
    queryFn: () => getStoryboard(projectId, { episode: resolvedEpisodeId }),
    // 集 id 还没解析出来（项目还在加载）就先不发：带缺参数的请求只会 400。
    enabled: projectId.length > 0 && resolvedEpisodeId !== undefined,
  });

  /**
   * M6 只读视图：和故事板同一集一起取（布局都在服务端，前端不预计算）。
   *
   * 刻意**不带视图条件**：只读投影很轻（16 镜级别），预取让切 tab 立刻有画面；
   * 也避免 `enabled` 依赖 UI 状态时，任何状态没对齐就永远停在 pending 的死角。
   * WS 收到 shot.* / snapshot 后按 300ms 防抖 invalidate，重新拉服务端布局。
   */
  const canvasQuery = useQuery({
    queryKey: ["canvas", projectId, resolvedEpisodeId ?? "pending"],
    queryFn: () => getCanvas(projectId, { episode: resolvedEpisodeId }),
    enabled: projectId.length > 0 && resolvedEpisodeId !== undefined,
  });

  /**
   * 画布素材带与「关联快捷缩略图」需要资料库实体（角色/场景/道具）。
   * 与画布一起预取：只读视图，代价很小；缺它画布就只有场块和镜头卡。
   */
  const libraryQuery = useQuery({
    queryKey: ["library", projectId, "canvas"],
    queryFn: () => getLibrary(projectId, { kind: "all" }),
    enabled: projectId.length > 0,
    staleTime: 30_000,
  });

  const timelineQuery = useQuery({
    queryKey: ["timeline", projectId, resolvedEpisodeId ?? "pending"],
    queryFn: () => getTimeline(projectId, { episode: resolvedEpisodeId }),
    enabled: projectId.length > 0 && resolvedEpisodeId !== undefined,
  });

  // 点数只用于顶栏展示：失败不阻塞工作台。
  const meQuery = useQuery({ queryKey: ["me"], queryFn: () => getMe() });

  /**
   * 历史列表同时给两处用：右栏「历史」tab，以及顶栏「撤销」是否可用。
   * 契约没有 `canUndo` 之类的探针，前端只能从「有没有自己的提交」推断；
   * 接口失败时 `retry: false` 静默降级为「不可撤销」，不阻塞工作台。
   */
  const historyQuery = useQuery({
    queryKey: ["history", projectId],
    queryFn: () => listProjectHistory(projectId, { limit: 60 }),
    enabled: projectId.length > 0,
    retry: false,
    staleTime: 5_000,
  });

  /** 右栏 tab 受控：画布 / 时间线点镜头时切到「镜头」。 */
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("agent");
  /** 「在画布看」要定位的镜头；进入画布后由 CanvasBoard 居中。 */
  const [canvasFocusShot, setCanvasFocusShot] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [batchDuration, setBatchDuration] = useState("3");
  const [pending, setPending] = useState(false);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>("idle");

  // M3：生成队列 / 预估确认层
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [pendingGeneration, setPendingGeneration] = useState<PendingGeneration | null>(null);
  const [estimating, setEstimating] = useState<GenerationKind | null>(null);
  const [submittingGeneration, setSubmittingGeneration] = useState(false);
  const [cancelingJobs, setCancelingJobs] = useState<ReadonlySet<string>>(new Set());

  // 工作台外壳：筛选 / 当前镜 / 右栏
  const [filter, setFilter] = useState<ShotFilter>("all");
  const [activeShotId, setActiveShotId] = useState<string | null>(null);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  /** 窄屏时右栏是抽屉：需要遮罩 / Esc / 焦点接管 */
  const [narrow, setNarrow] = useState(false);
  const [agentBusy, setAgentBusy] = useState(false);

  // M4：撤销 / 历史 / 用量 / 导出
  const [undoPending, setUndoPending] = useState(false);
  /** 本页成功提交的次数：即使历史接口不可用，也还能撤销自己刚做的改动。 */
  const [undoableCommits, setUndoableCommits] = useState(0);
  const [usageOpen, setUsageOpen] = useState(false);
  const [exportState, setExportState] = useState<ExportUiState>({ phase: "idle" });

  // G-05：左下角登录用户 → 用户设置浮层
  const [accountOpen, setAccountOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // M8：产出线浮层里的「新建 / 重命名」表单（同一套字段，两种模式）
  const [outputDialog, setOutputDialog] = useState<{
    mode: OutputDialogMode;
    output: OutputLine | null;
  } | null>(null);
  const [outputBusy, setOutputBusy] = useState(false);
  const [outputError, setOutputError] = useState<string | null>(null);

  // M5：资料库抽屉 / 分享弹层
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [framesOpen, setFramesOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [frameShotId, setFrameShotId] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryFocus, setLibraryFocus] = useState<LibraryFocusTarget | null>(null);
  /** 资料库主区视图的一级 tab（左栏二级导航也能切）。 */
  const [libraryTab, setLibraryTab] = useState<LibraryTab>("world");
  const [shareOpen, setShareOpen] = useState(false);

  const viewRef = useRef<StoryboardView | null>(null);
  const pendingRef = useRef(false);
  const streamRef = useRef<ProjectStream | null>(null);
  const hasConnectedRef = useRef(false);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const agentButtonRef = useRef<HTMLButtonElement>(null);
  const inspectorRef = useRef<HTMLElement>(null);
  const exportAbortRef = useRef<AbortController | null>(null);
  const canUndoRef = useRef(false);
  const undoRef = useRef<() => void>(() => {});
  const viewRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousViewModeRef = useRef<WorkspaceTab>(viewMode);
  const hub = useMemo(() => new StreamHub(), []);
  const { toasts, push, dismiss } = useToasts();

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  // 窄屏（<=1180px）自动收起右栏：1024px 下不出现横向滚动。
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(max-width: 1180px)");
    setNarrow(query.matches);
    if (query.matches) setInspectorOpen(false);
    const onChange = (event: MediaQueryListEvent) => {
      setNarrow(event.matches);
      if (event.matches) setInspectorOpen(false);
    };
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  /** 收起右栏：抽屉模式下把焦点还给顶栏入口，别把键盘用户留在空处。 */
  const closeInspector = useCallback(() => {
    setInspectorOpen(false);
    if (!narrow) return;
    setTimeout(() => agentButtonRef.current?.focus(), 0);
  }, [narrow]);

  /** 展开右栏：抽屉模式下把焦点移进面板（第一个 tab）。 */
  const openInspector = useCallback(() => {
    setInspectorOpen(true);
    if (!narrow) return;
    setTimeout(() => {
      const target = inspectorRef.current?.querySelector<HTMLElement>('[role="tab"], textarea, button');
      target?.focus();
    }, 0);
  }, [narrow]);

  const toggleInspector = useCallback(() => {
    if (inspectorOpen) closeInspector();
    else openInspector();
  }, [inspectorOpen, closeInspector, openInspector]);

  // 抽屉打开时 Esc 关闭（桌面常驻面板不抢 Esc）。
  useEffect(() => {
    if (!narrow || !inspectorOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeInspector();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [narrow, inspectorOpen, closeInspector]);

  /** 只取快照，不落 state：调用方要决定失败时是保留现状还是覆盖。 */
  const fetchJobs = useCallback(async (): Promise<GenerationJob[] | null> => {
    if (!projectId) return null;
    try {
      const list = await listGenerations(projectId, { limit: 20 });
      setQueueError(null);
      return replaceJobs([], list.items);
    } catch (error) {
      setQueueError(errorMessage(error, "生成队列加载失败"));
      return null;
    }
  }, [projectId]);

  /** 队列全量快照：首屏、断线重连都走它（服务端为准）。 */
  const refreshJobs = useCallback(async () => {
    const items = await fetchJobs();
    if (items) setJobs(items);
  }, [fetchJobs]);

  useEffect(() => {
    setJobs([]);
    void refreshJobs();
  }, [refreshJobs]);

  /** 事件里若带了余额，直接改本地 me 缓存，省一次往返。 */
  const applyEventBalance = useCallback(
    (payload: Record<string, unknown>) => {
      const value = payload.usablePoints ?? payload.balance;
      if (typeof value !== "number" || !Number.isFinite(value)) return;
      queryClient.setQueryData<Me>(["me"], (prev) =>
        prev ? { ...prev, miya: { ...prev.miya, usablePoints: value } } : prev,
      );
    },
    [queryClient],
  );

  const closeEstimate = useCallback(() => setPendingGeneration(null), []);

  /** 左轨「资料库」：可带 tab；不带就停在抽屉里的当前 tab。 */
  const openLibrary = useCallback((kind?: LibraryKind) => {
    setLibraryFocus(kind ? { kind } : null);
    setLibraryOpen(true);
  }, []);

  /** 镜头卡 chips：打开资料库并定位到该角色 / 场景。 */
  const focusLibraryEntity = useCallback((target: LibraryChipTarget) => {
    setLibraryFocus({ kind: target.kind, id: target.id ?? null, name: target.name });
    setLibraryOpen(true);
  }, []);

  const closeLibrary = useCallback(() => {
    setLibraryOpen(false);
    setLibraryFocus(null);
  }, []);

  const closeShare = useCallback(() => setShareOpen(false), []);

  // HTTP 查询结果作为初始真源；WS 已经推到更新的 rev 时不要被旧响应覆盖。
  useEffect(() => {
    const data = storyboardQuery.data;
    if (!data) return;
    setView((prev) => {
      if (prev && prev.episode.id === data.episode.id && prev.rev > data.rev) return prev;
      return data;
    });
  }, [storyboardQuery.data]);

  /**
   * 切到一条还没有集的产出线：把上一集的故事板清掉。
   * 否则镜头带 / 右栏还会显示**另一条产出线**的镜头，和左栏「该产出线还没有集」自相矛盾。
   */
  useEffect(() => {
    if (outputEmpty) setView(null);
  }, [outputEmpty]);

  // 换集 / 换项目 / 换产出线时清空多选与当前镜。
  useEffect(() => {
    setSelectedIds(new Set());
    setActiveShotId(null);
    setFilter("all");
  }, [episodeParam, projectId, currentOutputId]);

  // 故事板变化后剔除已经不存在的选中项。
  useEffect(() => {
    if (!view) return;
    const ids = new Set(view.shots.map((shot) => shot.id));
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setActiveShotId((prev) => {
      if (prev && ids.has(prev)) return prev;
      const first = [...view.shots].sort((a, b) => a.order - b.order)[0];
      return first ? first.id : null;
    });
  }, [view]);

  /**
   * 画布 / 时间线的布局由服务端算：收到 `shot.*` 或 `snapshot` 后只 invalidate，
   * 不在前端复刻布局算法。300ms 防抖，避免一轮批量生成打出几十个请求。
   */
  const scheduleCanvasRefresh = useCallback(() => {
    if (viewRefreshTimerRef.current) clearTimeout(viewRefreshTimerRef.current);
    viewRefreshTimerRef.current = setTimeout(() => {
      viewRefreshTimerRef.current = null;
      void queryClient.invalidateQueries({ queryKey: ["canvas", projectId] });
      void queryClient.invalidateQueries({ queryKey: ["timeline", projectId] });
    }, 300);
  }, [projectId, queryClient]);

  // 卸载时清掉防抖定时器（含切项目 / 离开工作台）。
  useEffect(
    () => () => {
      if (viewRefreshTimerRef.current) clearTimeout(viewRefreshTimerRef.current);
    },
    [],
  );

  // 一条 WebSocket：故事板消费结构事件，Agent 面板通过 hub 订阅 agent.*。
  useEffect(() => {
    if (!projectId) return;

    hasConnectedRef.current = false;

    function handleMessage(message: StreamMessage) {
      hub.emit(message);

      // 只读视图跟着服务端重排：任何镜头结构变化 / 全量快照都要 refetch。
      if (message.type === "snapshot" || message.type.startsWith("shot.")) {
        scheduleCanvasRefresh();
      }

      if (message.type === "snapshot") {
        const parsed = StoryboardView.safeParse(message.payload.view);
        if (!parsed.success) return;
        setView((prev) => {
          // 切换集后服务端的 snapshot 可能还是别的集，别把当前集盖掉。
          if (prev && prev.episode.id !== parsed.data.episode.id) return prev;
          return parsed.data;
        });
        return;
      }

      if (message.type === "job.updated") {
        applyEventBalance(message.payload);
        const job = jobFromEvent(message.payload);
        if (job) {
          setJobs((prev) => upsertJob(prev, job));
          if (isTerminalJob(job.status)) {
            void queryClient.invalidateQueries({ queryKey: ["me"] });
          }
          return;
        }
        // payload 只带 id：回查单 job，查不到再退回全量。
        const jobId = jobIdFromEvent(message.payload);
        if (jobId) {
          void getGeneration(projectId, jobId)
            .then((fetched) => setJobs((prev) => upsertJob(prev, fetched)))
            .catch(() => void refreshJobs());
        } else {
          void refreshJobs();
        }
        return;
      }

      if (message.type === "quota.warning") {
        const used = message.payload.usedPoints;
        const limit = message.payload.limitPoints;
        if (typeof used === "number" && typeof limit === "number") {
          push("error", `点数接近上限（${used}/${limit}）`);
        }
        return;
      }

      if (message.type === "ping" || message.type.startsWith("agent.")) return;

      // media.ready 在 applyProjectEvent 里落到镜头对应槽位。
      setView((prev) => (prev ? applyProjectEvent(prev, message) : prev));
    }

    const stream = new ProjectStream({
      projectId,
      onMessage: handleMessage,
      onStatus: (status) => {
        setStreamStatus(status);
        if (status !== "open") return;
        // 首次 open 之前已经拉过一次；之后的 open 只可能是重连，必须补全量。
        if (!hasConnectedRef.current) {
          hasConnectedRef.current = true;
          return;
        }
        void refreshJobs();
        void queryClient.invalidateQueries({ queryKey: ["me"] });
        // 契约 §2.9：重连成功后额外重取 storyboard view（snapshot 丢了也能恢复签名 URL）。
        void queryClient.invalidateQueries({ queryKey: ["storyboard", projectId] });
        void queryClient.invalidateQueries({ queryKey: ["canvas", projectId] });
        void queryClient.invalidateQueries({ queryKey: ["timeline", projectId] });
      },
    });
    streamRef.current = stream;
    stream.noteRev(viewRef.current?.rev ?? 0);
    stream.connect();

    return () => {
      stream.close();
      streamRef.current = null;
      setStreamStatus("idle");
    };
  }, [
    projectId,
    hub,
    push,
    refreshJobs,
    queryClient,
    applyEventBalance,
    scheduleCanvasRefresh,
  ]);

  const activeEpisodeId = resolvedEpisodeId ?? view?.episode.id;

  const selectEpisode = useCallback(
    (episodeId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (episodeId) next.set("ep", episodeId);
        else next.delete("ep");
        return next;
      });
    },
    [setSearchParams],
  );

  /**
   * 切产出线（契约 §3）：`?out=` 换线 + 清掉 `?ep=`，
   * 让「当前集」重新按新产出线的第一集解析 —— 左栏集列表跟着换。
   */
  const selectOutput = useCallback(
    (outputId?: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (outputId) next.set("out", outputId);
        else next.delete("out");
        next.delete("ep");
        return next;
      });
      setSelectedIds(new Set());
      setActiveShotId(null);
    },
    [setSearchParams],
  );

  /** 产出线写完之后：浮层（全量）和项目详情（面包屑 / 集归属）一起刷。 */
  const refreshOutputs = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["outputs", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["project", projectId] }),
    ]);
  }, [queryClient, projectId]);

  const openCreateOutput = useCallback(() => {
    setOutputError(null);
    setOutputDialog({ mode: "create", output: null });
  }, []);

  const openRenameOutput = useCallback((output: OutputLine) => {
    setOutputError(null);
    setOutputDialog({ mode: "rename", output });
  }, []);

  const closeOutputDialog = useCallback(() => {
    setOutputDialog(null);
    setOutputError(null);
  }, []);

  const handleCreateOutput = useCallback(
    async (input: CreateOutputInput) => {
      if (outputBusy) return;
      setOutputBusy(true);
      setOutputError(null);
      try {
        const created = await createOutput(projectId, input);
        await refreshOutputs();
        setOutputDialog(null);
        // 建完直接切过去：空产出线没有集，停在原地只会让人以为没生效。
        selectOutput(created.id);
        push("success", `已新建产出线「${created.title}」`);
      } catch (error) {
        setOutputError(errorMessage(error, "新建产出线失败"));
      } finally {
        setOutputBusy(false);
      }
    },
    [outputBusy, projectId, push, refreshOutputs, selectOutput],
  );

  /** 编辑产出线：标题 / 形态 / 画幅一起提交（服务端 M8 起三者都可改）。 */
  const handleRenameOutput = useCallback(
    async (input: CreateOutputInput) => {
      const target = outputDialog?.output;
      if (!target || outputBusy) return;
      setOutputBusy(true);
      setOutputError(null);
      try {
        await patchOutput(projectId, target.id, {
          title: input.title,
          kind: input.kind,
          aspect: input.aspect,
        });
        await refreshOutputs();
        setOutputDialog(null);
        push("success", `已保存「${input.title}」`);
      } catch (error) {
        setOutputError(errorMessage(error, "保存产出线失败"));
      } finally {
        setOutputBusy(false);
      }
    },
    [outputBusy, outputDialog?.output, projectId, push, refreshOutputs],
  );

  /**
   * 把一集移到别的产出线（契约 §3：删线前先把集移走）。
   *
   * 如果移走的正是当前打开的那一集，要把 `?ep=` 清掉：否则「集定位优先」会让
   * 画面跟着这集跳到目标线，而用户以为自己还在原来的线上。
   */
  const handleMoveEpisode = useCallback(
    async (episodeId: string, outputId: string) => {
      if (outputBusy) return;
      setOutputBusy(true);
      try {
        const moved = await moveEpisode(projectId, episodeId, outputId);
        await refreshOutputs();
        if (episodeParam === episodeId) selectEpisode("");
        const target = outputs.find((output) => output.id === moved.outputId);
        push(
          "success",
          `第 ${moved.no} 集已移到「${target?.title ?? moved.outputId}」`,
        );
      } catch (error) {
        push("error", errorMessage(error, "移动这一集失败"));
      } finally {
        setOutputBusy(false);
      }
    },
    [
      episodeParam,
      outputBusy,
      outputs,
      projectId,
      push,
      refreshOutputs,
      selectEpisode,
    ],
  );

  const handlePatchOutput = useCallback(
    async (output: OutputLine, patch: PatchOutputInput) => {
      if (outputBusy) return;
      setOutputBusy(true);
      try {
        await patchOutput(projectId, output.id, patch);
        await refreshOutputs();
        if (patch.archived === true) push("success", `「${output.title}」已归档（只读）`);
        else if (patch.hidden === true) push("success", `「${output.title}」已隐藏`);
        else if (patch.hidden === false || patch.archived === false) {
          push("success", `「${output.title}」已恢复显示`);
        }
      } catch (error) {
        push("error", errorMessage(error, "产出线更新失败"));
      } finally {
        setOutputBusy(false);
      }
    },
    [outputBusy, projectId, push, refreshOutputs],
  );

  /**
   * 删除产出线：409 `output_in_use` 要说清楚「还有 N 集」（契约 §4 验收 4），
   * 422 说明是最后一条。前端不做二次确认弹窗，删除按钮在二级菜单里已经够深。
   */
  const handleDeleteOutput = useCallback(
    async (output: OutputLine) => {
      if (outputBusy) return;
      setOutputBusy(true);
      try {
        await deleteOutput(projectId, output.id);
        await refreshOutputs();
        if (currentOutputId === output.id) selectOutput(undefined);
        push("success", `已删除产出线「${output.title}」`);
      } catch (error) {
        const blocked = outputDeleteBlockedMessage(error);
        push("error", blocked ?? errorMessage(error, "删除产出线失败"));
      } finally {
        setOutputBusy(false);
      }
    },
    [currentOutputId, outputBusy, projectId, push, refreshOutputs, selectOutput],
  );

  const submit = useCallback(
    async (commands: Command[], successMessage?: string): Promise<boolean> => {
      if (commands.length === 0) return false;
      if (pendingRef.current) {
        push("info", "上一批命令还在提交，请稍候");
        return false;
      }
      const current = viewRef.current;
      if (!current) return false;

      pendingRef.current = true;
      setPending(true);

      const optimistic = applyCommandsLocally(current, commands);
      viewRef.current = optimistic;
      setView(optimistic);

      try {
        const output = await postCommands(projectId, { baseRev: current.rev, commands });
        const settled = { ...optimistic, rev: output.rev };
        viewRef.current = settled;
        setView(settled);
        streamRef.current?.noteRev(output.rev);
        setUndoableCommits((count) => count + 1);
        if (successMessage) push("success", successMessage);
        return true;
      } catch (error) {
        const conflict = conflictViewFrom(error);
        if (conflict) {
          viewRef.current = conflict;
          setView(conflict);
          streamRef.current?.noteRev(conflict.rev);
          push("error", "故事板已被更新，已同步到最新版本");
        } else {
          viewRef.current = current;
          setView(current);
          push("error", errorMessage(error, "提交失败，已撤销本地修改"));
        }
        return false;
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    },
    [projectId, push],
  );

  /**
   * 重取当前集的故事板（撤销 / 回滚之后用）。
   *
   * 撤销和回滚的响应都不带完整投影，`GET /view` 才是唯一真源；
   * 同时把 react-query 缓存对齐，避免下一次 effect 用旧数据盖回来。
   */
  const refreshView = useCallback(async (): Promise<void> => {
    if (!projectId || resolvedEpisodeId === undefined) return;
    try {
      const data = await getStoryboard(projectId, { episode: resolvedEpisodeId });
      setView((prev) => {
        // 刷新期间 WS 可能已经推到更新的 rev，别被慢响应盖回去。
        if (prev && prev.episode.id === data.episode.id && prev.rev > data.rev) {
          viewRef.current = prev;
          return prev;
        }
        viewRef.current = data;
        return data;
      });
      streamRef.current?.noteRev(data.rev);
      queryClient.setQueryData(["storyboard", projectId, resolvedEpisodeId], data);
    } catch {
      // 刷新失败不打断操作：下一次 WS 事件 / 查询会补上。
    }
  }, [projectId, resolvedEpisodeId, queryClient]);

  /** 撤销（契约 M4 §1）：无参数，回滚自己上一个提交；409 用返回的 view 覆盖。 */
  const handleUndo = useCallback(async (): Promise<void> => {
    if (!projectId || !viewRef.current) return;
    setUndoPending(true);
    try {
      await undoProject(projectId);
      setUndoableCommits((count) => Math.max(0, count - 1));
      await refreshView();
      void queryClient.invalidateQueries({ queryKey: ["history", projectId] });
      push("success", "已撤销");
    } catch (error) {
      const conflict = conflictViewFrom(error);
      if (conflict) {
        viewRef.current = conflict;
        setView(conflict);
        streamRef.current?.noteRev(conflict.rev);
        push("error", "内容已被更新，已同步到最新版本");
      } else {
        push("error", errorMessage(error, "撤销失败，请稍后重试"));
      }
    } finally {
      setUndoPending(false);
    }
  }, [projectId, refreshView, queryClient, push]);

  /** 回滚成功：历史面板已经拿到 `RevertOutput`，这里统一刷新 + 提示。 */
  const handleReverted = useCallback(
    (output: RevertOutput, toRev: number) => {
      streamRef.current?.noteRev(output.rev);
      setUndoableCommits((count) => count + 1);
      void refreshView();
      void queryClient.invalidateQueries({ queryKey: ["history", projectId] });
      push(
        "success",
        output.rev === toRev
          ? `已回滚到 rev ${toRev}`
          : `已回滚到 rev ${toRev}（新版本 rev ${output.rev}）`,
      );
    },
    [refreshView, queryClient, projectId, push],
  );

  /** zip 导出：创建任务 → 2s 轮询（最多 60 次）→ 给签名下载链接。 */
  const handleExportZip = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    if (exportState.phase === "creating" || exportState.phase === "polling") return;

    exportAbortRef.current?.abort();
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setExportState({ phase: "creating" });

    try {
      const created = await createExport(projectId, { kind: "zip", includeMedia: true });
      setExportState({
        phase: "polling",
        exportId: created.exportId,
        status: created.status,
        attempts: 0,
      });

      const result = await pollExportJob({
        signal: controller.signal,
        fetchOnce: async (attempt) => {
          const job = await getExport(projectId, created.exportId, { signal: controller.signal });
          setExportState({
            phase: "polling",
            exportId: created.exportId,
            status: job.status,
            attempts: attempt,
          });
          return job;
        },
      });

      if (result.timedOut) {
        setExportState({
          phase: "timeout",
          message: `已检查 ${result.attempts} 次仍未完成。打包可能还在继续，稍后重新导出即可。`,
        });
        return;
      }

      if (result.job.status === "succeeded") {
        setExportState({ phase: "done", job: result.job });
      } else {
        setExportState({
          phase: "failed",
          message: result.job.error ?? "导出失败，请重新导出。",
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (error instanceof ApiError && error.status === 403) {
        // 套餐未开通 `export_zip`：重试也是 403，只给文案，不给重试。
        setExportState({ phase: "forbidden", message: EXPORT_FORBIDDEN_MESSAGE });
        push("error", EXPORT_FORBIDDEN_MESSAGE);
        return;
      }
      setExportState({
        phase: "failed",
        message: errorMessage(error, "导出失败，请稍后重试"),
      });
    }
  }, [projectId, exportState.phase, push]);

  /** 打印故事板：打开自包含打印视图，等加载完再调 `print()`。 */
  const handlePrintStoryboard = useCallback((): void => {
    if (!projectId || !activeEpisodeId) {
      push("info", "先选择一集再打印");
      return;
    }
    const win = window.open(printViewPath(projectId, activeEpisodeId), "_blank");
    if (!win) {
      push("error", "浏览器拦截了打印窗口，请允许弹窗后重试");
      return;
    }
    push("info", "打印视图已打开，用浏览器「另存为 PDF」即可");
    const trigger = () => {
      try {
        win.focus();
        win.print();
      } catch {
        // 打印被拒（或环境不支持）不影响工作台。
      }
    };
    if (win.document?.readyState === "complete") trigger();
    else win.addEventListener?.("load", trigger, { once: true });
  }, [projectId, activeEpisodeId, push]);

  function handleDurationCommit(shotId: string, duration: number) {
    if (!Number.isFinite(duration) || duration < MIN_DURATION || duration > MAX_DURATION) {
      push("error", "时长需在 0.5–15 秒之间");
      return;
    }
    const shot = viewRef.current?.shots.find((item) => item.id === shotId);
    if (!shot || shot.duration === duration) return;
    void submit([{ type: "update_shot", shotId, patch: { duration } }]);
  }

  function handleDialogueCommit(shotId: string, lines: DialogueLine[]) {
    if (lines.length > 6) {
      push("error", "每镜最多 6 句台词");
      return;
    }
    if (lines.some((line) => line.text.length > 120)) {
      push("error", "单句台词最多 120 字");
      return;
    }
    const shot = viewRef.current?.shots.find((item) => item.id === shotId);
    if (!shot || sameDialogue(shot.dialogue, lines)) return;
    void submit([{ type: "set_dialogue", shotId, lines }]);
  }

  function handleReorder(sceneId: string, orderedIds: string[]) {
    const scene = viewRef.current?.scenes.find((item) => item.id === sceneId);
    if (!scene) return;
    const current = new Set(scene.shotIds);
    const sameSet =
      current.size === orderedIds.length && orderedIds.every((id) => current.has(id));
    if (!sameSet) {
      push("error", "排序结果与该场镜头不一致");
      return;
    }
    if (scene.shotIds.every((id, index) => id === orderedIds[index])) return;
    void submit([{ type: "reorder_shots", sceneId, orderedIds }]);
  }

  function toggleSelect(shotId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(shotId)) next.delete(shotId);
      else next.add(shotId);
      return next;
    });
  }

  /** 镜头带 / 右栏「镜头」tab：定位到这一镜。 */
  const focusShot = useCallback((shotId: string) => {
    setActiveShotId(shotId);
    const node = boardRef.current?.querySelector<HTMLElement>(`[data-shot-id="${shotId}"]`);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, []);

  /** 画布 / 时间线点镜头：选中态共用，右栏直接切到「镜头」。 */
  const focusShotFromView = useCallback(
    (shotId: string) => {
      setActiveShotId(shotId);
      setInspectorTab("shot");
      if (narrow) setInspectorOpen(true);
    },
    [narrow],
  );

  const selectShotOnCanvas = useCallback(
    (shotId: string) => {
      // 用户自己点卡：清掉「在画布看」的待定位目标。
      setCanvasFocusShot(null);
      focusShotFromView(shotId);
    },
    [focusShotFromView],
  );

  const changeView = useCallback(
    (mode: WorkspaceTab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const value = viewParamValue(mode);
        if (value) next.set("view", value);
        else next.delete("view");
        return next;
      });
      if (mode !== "canvas") setCanvasFocusShot(null);
    },
    [setSearchParams],
  );

  /** 故事板 / 右栏「在画布看」：切到画布并让该镜居中。 */
  const openCanvasAtShot = useCallback(
    (shotId: string) => {
      setActiveShotId(shotId);
      setInspectorTab("shot");
      setCanvasFocusShot(shotId);
      changeView("canvas");
    },
    [changeView],
  );

  /** 时间线「在故事板打开」：切回故事板并滚到该镜（选中态一起带过去）。 */
  const openStoryboardAtShot = useCallback(
    (shotId: string) => {
      setActiveShotId(shotId);
      setInspectorTab("shot");
      changeView("storyboard");
    },
    [changeView],
  );

  // 从画布 / 时间线切回故事板时，把当前镜滚到视口中央（视图切换不丢选中镜）。
  useEffect(() => {
    const previous = previousViewModeRef.current;
    previousViewModeRef.current = viewMode;
    if (viewMode !== "storyboard" || previous === "storyboard" || !activeShotId) return;
    const timer = window.setTimeout(() => {
      const node = boardRef.current?.querySelector<HTMLElement>(
        `[data-shot-id="${activeShotId}"]`,
      );
      node?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [viewMode, activeShotId]);

  /**
   * 资料库 / 剧本里点「在故事板看这一场」：切回故事板并选中该场第一镜。
   * 场次可能不在当前集（世界树跨集），`episodeNo` 命中时先切集。
   */
  const openStoryboardAtScene = useCallback(
    (sceneId: string, episodeNo?: number) => {
      const targetEpisode =
        episodeNo === undefined
          ? undefined
          : project?.episodes.find((episode) => episode.no === episodeNo);
      if (targetEpisode && targetEpisode.id !== activeEpisodeId) selectEpisode(targetEpisode.id);
      const shot = viewRef.current?.shots.find((item) => item.sceneId === sceneId);
      if (shot) {
        setActiveShotId(shot.id);
        setInspectorTab("shot");
      }
      changeView("storyboard");
    },
    [project?.episodes, activeEpisodeId, selectEpisode, changeView],
  );

  /** 大纲 / 剧本里的「产出线」卡片：M8 起真的切过去（并回到故事板）。 */
  const openOutputHint = useCallback(
    (outputId: string) => {
      selectOutput(outputId);
      changeView("storyboard");
    },
    [changeView, selectOutput],
  );

  /** 左栏「内容导航」：切主区视图（资料库 / 大纲 / 剧本 / 数据），不是浮层。 */
  const openContent = useCallback(
    (content: WorkspaceContentView) => {
      changeView(content);
    },
    [changeView],
  );

  /** 左栏资料库二级项 / 统计行：进资料库主区视图并定位到该一级分类。 */
  const openLibraryTab = useCallback(
    (target?: LibraryTab) => {
      if (target) setLibraryTab(target);
      changeView("library");
    },
    [changeView],
  );

  /** `/`：聚焦 Agent 输入（必要时先展开右栏）。 */
  const focusAgentInput = useCallback(() => {
    setInspectorTab("agent");
    setInspectorOpen(true);
    window.setTimeout(() => {
      const input = inspectorRef.current?.querySelector<HTMLTextAreaElement>("#agent-input");
      if (input) input.focus();
      else agentButtonRef.current?.focus();
    }, 0);
  }, []);

  /** `A`：收起 / 展开右栏（原型 `toggleAgent`：展开时落在 Agent tab）。 */
  const toggleAgentPanel = useCallback(() => {
    if (inspectorOpen) {
      closeInspector();
      return;
    }
    setInspectorTab("agent");
    openInspector();
  }, [inspectorOpen, closeInspector, openInspector]);

  /**
   * 快捷键（Wiki G-09 / 原型 `init` 3805）：
   * `1/2/3` 视图、`L` 资料库、`O` 大纲、`S` 剧本、`A` Agent、`D` 数据、`/` 聚焦输入。
   * 输入框里不抢键；带修饰键的组合留给浏览器（⌘S 等）。
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "Escape") return; // 弹层各自处理 Esc，这里不重复
      // 有弹层时不吃快捷键（原型：sheet 打开时整段跳过），免得在弹层后面切视图。
      if (libraryOpen || usageOpen || shareOpen || accountOpen || framesOpen || shortcutsOpen || pendingGeneration) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === "/") {
        event.preventDefault();
        focusAgentInput();
        return;
      }

      // `?`（Shift + /）打开快捷操作浮层：和原型一样，说明收在浮层里而不是常驻横条。
      if (event.key === "?") {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      if (event.key.toLowerCase() === "a") {
        event.preventDefault();
        toggleAgentPanel();
        return;
      }

      const tab = tabForKey(event.key);
      if (!tab) return;
      event.preventDefault();
      if (tab === "library") {
        openLibraryTab();
        return;
      }
      changeView(tab);
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [
    changeView,
    focusAgentInput,
    openLibraryTab,
    toggleAgentPanel,
    libraryOpen,
    usageOpen,
    shareOpen,
    accountOpen,
    framesOpen,
    shortcutsOpen,
    pendingGeneration,
  ]);

  /** 退出登录：清掉缓存后回登录页（`POST /api/auth/logout`）。 */
  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await logout();
      queryClient.clear();
      setAccountOpen(false);
      navigate("/login", { replace: true });
    } catch (error) {
      push("error", errorMessage(error, "退出失败，请稍后重试"));
    } finally {
      setLoggingOut(false);
    }
  }, [navigate, push, queryClient]);

  /** 确认单镜：写回 status=approved。 */
  const confirmShot = useCallback(
    (shotId: string) => {
      const shot = viewRef.current?.shots.find((item) => item.id === shotId);
      if (!shot || shot.status === "approved") return;
      void submit(
        [{ type: "update_shot", shotId, patch: { status: "approved" } }],
        `镜头 ${shot.order} 已确认`,
      );
    },
    [submit],
  );

  /** 确认整集：把还没确认的镜头一次写回（一条命令一个镜头，最多 200 条）。 */
  const confirmEpisode = useCallback(() => {
    const shots = viewRef.current?.shots ?? [];
    const commands: Command[] = shots
      .filter((shot) => shot.status !== "approved")
      .slice(0, 200)
      .map((shot) => ({ type: "update_shot", shotId: shot.id, patch: { status: "approved" } }));
    void submit(commands, `已确认 ${commands.length} 个镜头`);
  }, [submit]);

  const shotOrderById = useMemo(
    () => new Map((view?.shots ?? []).map((shot) => [shot.id, shot.order] as const)),
    [view],
  );

  /**
   * 画布生成队列的候选镜头：和故事板共用 `selectedIds`，
   * 因此在画布勾的镜、在故事板选的镜是同一份选择（C-13）。
   */
  const canvasQueueShots = useMemo(
    () =>
      (view?.shots ?? []).map((shot) => ({
        id: shot.id,
        order: shot.order,
        label: `第 ${shot.order} 镜`,
        action: shot.action,
        thumbUrl: shot.keyframe?.url ?? null,
      })),
    [view],
  );

  /** 选中镜头 → 先预估（不扣点）→ 弹确认层。`explicitIds` 用于单镜重抽。 */
  const requestEstimate = useCallback(
    async (kind: GenerationKind, explicitIds?: string[]) => {
      const current = viewRef.current;
      if (!current || !projectId) return;
      const shotIds =
        explicitIds ??
        current.shots
          .filter((shot) => selectedIds.has(shot.id))
          .sort((a, b) => a.order - b.order)
          .map((shot) => shot.id);
      if (shotIds.length === 0) {
        push("info", "请先选择镜头");
        return;
      }

      setEstimating(kind);
      try {
        const estimate = await estimateGeneration(projectId, { kind, shotIds });
        setPendingGeneration({ kind, shotIds, estimate });
      } catch (error) {
        push("error", errorMessage(error, "预估失败，请稍后重试"));
      } finally {
        setEstimating(null);
      }
    },
    [projectId, selectedIds, push],
  );

  const regenerateShot = useCallback(
    (shotId: string) => {
      setActiveShotId(shotId);
      setSelectedIds(new Set([shotId]));
      void requestEstimate("keyframe", [shotId]);
    },
    [requestEstimate],
  );

  /** 确认后才真正建 job（带 Idempotency-Key，重复点不会重复扣费）。 */
  async function confirmGeneration() {
    if (!pendingGeneration || submittingGeneration) return;
    const { kind, shotIds, estimate } = pendingGeneration;
    setSubmittingGeneration(true);
    try {
      const output = await createGeneration(projectId, { kind, shotIds });

      // 创建响应没有 items：先占位让队列立刻出现，再用服务端全量对齐。
      const placeholder: GenerationJob = {
        id: output.jobId,
        projectId,
        kind,
        status: output.status,
        items: shotIds.map((shotId, index) => ({
          id: `${output.jobId}_${index + 1}`,
          shotId,
          status: "queued" as const,
          media: null,
          points: 0,
          error: null,
        })),
        estimatedPoints: output.estimatedPoints,
        reservedPoints: output.reservedPoints,
        settledPoints: 0,
        refundedPoints: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      setJobs((prev) => upsertJob(prev, placeholder));
      setPendingGeneration(null);
      setSelectedIds(new Set());

      // 服务端列表可能还没包含刚建的 job（最终一致），此时保留本地占位。
      const remote = await fetchJobs();
      setJobs((prev) => {
        if (!remote) return prev;
        return remote.some((job) => job.id === output.jobId) ? remote : upsertJob(remote, placeholder);
      });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      push(
        "success",
        `已提交${GENERATION_KIND_META[kind].label}生成，预估 ${formatPoints(estimate.estimatedPoints)} 点`,
      );
    } catch (error) {
      // 402 / 403 由服务端在提交前拦；确认层保留，用户可改选后重试。
      push("error", errorMessage(error, "提交生成失败"));
    } finally {
      setSubmittingGeneration(false);
    }
  }

  async function handleCancelJob(jobId: string) {
    setCancelingJobs((prev) => new Set(prev).add(jobId));
    try {
      const job = await cancelGeneration(projectId, jobId);
      setJobs((prev) => upsertJob(prev, job));
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      push("success", "已取消生成任务");
    } catch (error) {
      push("error", errorMessage(error, "取消失败，请稍后重试"));
    } finally {
      setCancelingJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  }

  async function handleBatchDuration() {
    const duration = Number(batchDuration);
    if (!Number.isFinite(duration) || duration < MIN_DURATION || duration > MAX_DURATION) {
      push("error", "时长需在 0.5–15 秒之间");
      return;
    }
    const current = viewRef.current;
    if (!current) return;
    const selected = current.shots.filter((shot) => selectedIds.has(shot.id));
    if (selected.length === 0) return;

    const commands: Command[] = selected.map((shot) => ({
      type: "update_shot",
      shotId: shot.id,
      patch: { duration },
    }));
    const ok = await submit(commands, `已批量更新 ${commands.length} 个镜头`);
    if (ok) setSelectedIds(new Set());
  }

  /** 弹层标题用「第几镜」，和卡片上的一致。 */
  const frameShotLabel = (() => {
    const shot = view?.shots.find((item) => item.id === frameShotId);
    if (!shot) return "";
    const scene = view?.scenes.find((item) => item.id === shot.sceneId);
    return `第 ${shot.order} 镜${scene ? ` · ${scene.name}` : ""}`;
  })();

  const editing: StoryboardEditing = {
    disabled: pending,
    selectedIds,
    onToggleSelect: toggleSelect,
    onDurationCommit: handleDurationCommit,
    onDialogueCommit: handleDialogueCommit,
    onReorder: handleReorder,
    onConfirmShot: confirmShot,
    onRegenerateShot: regenerateShot,
    onFocusLibrary: focusLibraryEntity,
    activeShotId,
    onOpenCanvas: openCanvasAtShot,
    onOpenFrames: (shotId) => {
      setFrameShotId(shotId);
      setFramesOpen(true);
    },
  };

  const pointsLabel = meQuery.isPending
    ? "…"
    : meQuery.isError
      ? "—"
      : formatPoints(meQuery.data?.miya.usablePoints);

  // M4：撤销可用性。历史里出现自己的提交（actor = 账号 id）才算「有可撤销的」，
  // 本页刚提交过也算——历史接口失败时至少还能撤销自己刚做的改动。
  const accountId = meQuery.data?.accountId ?? null;
  const historyItems = historyQuery.data?.items ?? [];
  const hasUndoableCommit = undoableCommits > 0 || hasOwnCommit(historyItems, accountId);
  const canUndo = view !== null && !pending && !undoPending && hasUndoableCommit;
  const undoHint = !view
    ? "工作台还没加载完成"
    : !hasUndoableCommit
      ? "还没有可撤销的提交"
      : "撤销你在这部剧里的上一个提交（⌘Z / Ctrl+Z）";

  canUndoRef.current = canUndo;
  undoRef.current = () => {
    if (canUndoRef.current) void handleUndo();
  };

  // Cmd/Ctrl+Z：焦点在输入框里时不抢（那是浏览器自己的「撤销输入」）。
  // 用 ref 读最新状态，避免每次 view 变化都重绑监听。
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (event.key.toLowerCase() !== "z") return;
      if (isEditableTarget(event.target)) return;
      if (!canUndoRef.current) return;
      event.preventDefault();
      undoRef.current();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // 离开工作台就停止轮询：别让已卸载页面的定时器继续打接口。
  useEffect(() => () => exportAbortRef.current?.abort(), []);

  const exportBusy = exportState.phase === "creating" || exportState.phase === "polling";
  const exportDisabled = view === null;
  const exportDisabledHint = "故事板加载完成后才能导出";

  const loadError = storyboardQuery.error ?? projectQuery.error;
  /**
   * 故事板一拿到 /view 就渲染：项目详情（名称 / 集列表）慢或失败时，
   * 不能把整个编辑区一起卡在「正在加载工作台」。左轨会回退到 /view 的当前集。
   */
  const isLoading = view === null && (projectQuery.isPending || storyboardQuery.isPending);
  const hasError = view === null && (projectQuery.isError || storyboardQuery.isError);

  const shots = view?.shots ?? [];
  const counts = countByStatus(shots);
  const visibleCount = shots.filter((shot) => matchesFilter(shot, filter)).length;
  const activeShot = view?.shots.find((shot) => shot.id === activeShotId) ?? null;
  const activeScene = activeShot && view ? findScene(view.scenes, activeShot.sceneId) ?? null : null;
  const sceneNames = (view?.scenes ?? []).map((scene) => scene.name);
  const duration = shots.reduce((sum, shot) => sum + shot.duration, 0);
  const styleLabel = project ? STYLE_LABEL[project.style] : "";
  const pendingReview = counts.total - counts.approved;

  const quickPrompts = useMemo(() => {
    if (!view || view.shots.length === 0) return [];
    const first = [...view.shots].sort((a, b) => a.order - b.order)[0];
    const prompts = ["哪些镜头还没确认", "这一集的节奏怎么调"];
    if (first) prompts.unshift(`把 ${shortId(first.id)} 的台词缩短`);
    return prompts;
  }, [view]);

  return (
    <div className="dp-ws" data-inspector={inspectorOpen ? "open" : "closed"}>
      <Rail
        project={project ?? null}
        view={view}
        activeEpisodeId={activeEpisodeId}
        onSelectEpisode={selectEpisode}
        viewMode={viewMode}
        onOpenContent={openContent}
        libraryTab={libraryTab}
        onSelectLibraryTab={openLibraryTab}
        outputs={outputs}
        currentOutputId={currentOutputId}
        currentOutputTitle={currentOutputTitle}
        onSelectOutput={selectOutput}
        onCreateOutput={openCreateOutput}
        onRenameOutput={openRenameOutput}
        onPatchOutput={(output, patch) => void handlePatchOutput(output, patch)}
        onDeleteOutput={(output) => void handleDeleteOutput(output)}
        onMoveEpisode={(episodeId, outputId) => void handleMoveEpisode(episodeId, outputId)}
        outputBusy={outputBusy}
        account={summarizeAccount(meQuery.data)}
        accountLoading={meQuery.isPending}
        accountOpen={accountOpen}
        onOpenAccount={() => setAccountOpen(true)}
        loading={projectQuery.isPending}
        switcherOpen={switcherOpen}
        onToggleSwitcher={() => setSwitcherOpen((open) => !open)}
        switcherSlot={
          <ProjectSwitcher
            open={switcherOpen}
            currentProjectId={projectId}
            projects={projectsQuery.data?.items ?? []}
            loading={projectsQuery.isPending}
            onClose={() => setSwitcherOpen(false)}
            onSwitch={(id) => {
              setSwitcherOpen(false);
              if (id !== projectId) navigate(`/p/${id}`);
            }}
            onOpenAll={() => {
              setSwitcherOpen(false);
              navigate("/");
            }}
            onNewProject={() => {
              setSwitcherOpen(false);
              navigate("/");
            }}
          />
        }
      />

      <main className="dp-main">
        <Topbar
          project={project ?? null}
          outputTitle={currentOutputTitle}
          view={view}
          activeEpisodeId={activeEpisodeId}
          episodes={outputEpisodes}
          onSelectEpisode={selectEpisode}
          viewMode={viewMode}
          onChangeView={changeView}
          pending={pending}
          streamStatus={streamStatus}
          pointsLabel={pointsLabel}
          inspectorOpen={inspectorOpen}
          onToggleInspector={toggleInspector}
          onToggleTheme={() => push("info", "浅色主题施工中，先只打磨暗色")}
          agentButtonRef={agentButtonRef}
          agentBusy={agentBusy}
          canUndo={canUndo}
          undoPending={undoPending}
          undoHint={undoHint}
          onUndo={() => void handleUndo()}
          exportBusy={exportBusy}
          exportDisabled={exportDisabled}
          exportDisabledHint={exportDisabledHint}
          onExportZip={() => void handleExportZip()}
          onPrintStoryboard={handlePrintStoryboard}
          onOpenShare={() => setShareOpen(true)}
        />

        {viewMode === "storyboard" ? (
          <>
            <ShotStrip view={view} activeShotId={activeShotId} onSelect={focusShot} />

            <section className="dp-viewport" aria-label="故事板">
          <div className="dp-board dp-scroll" ref={boardRef}>
            <div className="dp-board-inner">
              {hasError ? (
                <ErrorState
                  title="工作台加载失败"
                  message={errorMessage(loadError, "项目或故事板加载失败")}
                  onRetry={() => {
                    void projectQuery.refetch();
                    void storyboardQuery.refetch();
                  }}
                />
              ) : noEpisodes ? (
                <EmptyState
                  title="这个项目还没有剧集"
                  description="故事板按集组织；第一集建好之后，镜头就会出现在这里。"
                />
              ) : outputEmpty ? (
                <EmptyState
                  title="这条产出线还没有集"
                  description="新建产出线不会自动建集：切回别的产出线，或先在这条线下加一集。"
                />
              ) : isLoading || !view ? (
                <LoadingState label="正在加载工作台…" />
              ) : (
                <>
                  <div className="dp-ep-head">
                    <div style={{ minWidth: 0 }}>
                      <p className="dp-ep-eyebrow">
                        {view.episode.aspect}
                        {styleLabel ? ` · ${styleLabel}` : ""} · {view.scenes.length} 场
                      </p>
                      <h2 className="dp-ep-title">
                        {view.episode.title}
                        <small>第 {view.episode.no} 话</small>
                      </h2>
                      {sceneNames.length > 0 ? (
                        <p className="dp-ep-summary">场次：{sceneNames.join(" / ")}</p>
                      ) : null}
                      <p className="dp-ep-meta">
                        <span>
                          <b>{view.stats.shotCount}</b> 镜
                        </span>
                        <span>
                          <b>{formatClock(duration)}</b> 总时长
                        </span>
                        <span>
                          <b>{view.scenes.length}</b> 场
                        </span>
                        <span>
                          <b>{view.stats.usedPoints.toLocaleString("zh-CN")}</b> 点
                        </span>
                        <span>
                          画幅 {view.episode.aspect} · rev {view.rev}
                        </span>
                        <span title={project ? formatDateTime(project.updatedAt) : undefined}>
                          已确认 {view.stats.approvedCount}/{view.stats.shotCount}
                        </span>
                      </p>
                    </div>
                    <div className="dp-ep-actions">
                      <button
                        type="button"
                        className="dp-btn primary"
                        onClick={confirmEpisode}
                        disabled={pending || pendingReview === 0}
                        title={
                          counts.total === 0
                            ? "本集还没有镜头"
                            : pendingReview === 0
                              ? "全部镜头都已确认"
                              : `把剩下 ${pendingReview} 个镜头标为已确认`
                        }
                      >
                        确认整集
                      </button>
                      <p className="dp-ep-hint">
                        {counts.total === 0
                          ? "还没有镜头"
                          : pendingReview === 0
                            ? "整集已确认"
                            : `${pendingReview} 个镜头还没确认`}
                      </p>
                    </div>
                  </div>

                  <div className="dp-toolbar">
                    <div className="dp-filter-pills" role="group" aria-label="筛选镜头">
                      {FILTERS.map((item) => (
                        <button
                          key={item}
                          type="button"
                          aria-pressed={filter === item}
                          onClick={() => setFilter(item)}
                        >
                          {FILTER_LABEL[item]}
                          {item === "all"
                            ? ` · ${counts.total}`
                            : counts[item] > 0
                              ? ` · ${counts[item]}`
                              : ""}
                        </button>
                      ))}
                    </div>
                    <span className="dp-tiny-note">
                      顺序 = shot.order · 共 {filter === "all" ? counts.total : visibleCount} 镜可见
                    </span>
                    <span className="dp-toolbar-grow" />
                    <GenerationControls
                      selectedCount={selectedIds.size}
                      busyKind={estimating}
                      disabled={pending}
                      onRequest={(kind) => void requestEstimate(kind)}
                    />
                  </div>

                  {selectedIds.size > 0 ? (
                    <div role="region" aria-label="批量操作" className="dp-selbar">
                      <span style={{ fontWeight: 500 }}>已选 {selectedIds.size} 镜</span>
                      <label>
                        统一时长
                        <input
                          aria-label="批量时长"
                          type="number"
                          min={MIN_DURATION}
                          max={MAX_DURATION}
                          step={0.1}
                          value={batchDuration}
                          onChange={(event) => setBatchDuration(event.target.value)}
                        />
                        秒
                      </label>
                      <button
                        type="button"
                        onClick={() => void handleBatchDuration()}
                        disabled={pending}
                        className="dp-btn sm primary"
                      >
                        批量改时长
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedIds(new Set())}
                        className="dp-btn sm"
                      >
                        清空选择
                      </button>
                    </div>
                  ) : null}

                  <GenerationQueue
                    jobs={jobs}
                    shotOrderById={shotOrderById}
                    cancelingIds={cancelingJobs}
                    onCancel={(jobId) => void handleCancelJob(jobId)}
                    error={queueError}
                  />

                  <div aria-busy={pending} style={{ marginTop: 8 }}>
                    <Storyboard view={view} editing={editing} filter={filter} />
                  </div>
                </>
              )}
            </div>
          </div>
            </section>
          </>
        ) : viewMode === "canvas" ? (
          <section className="dp-viewport dp-viewport-view" aria-label="画布">
            {canvasQuery.isError ? (
              <div className="dp-view-state">
                <ErrorState
                  title="画布加载失败"
                  message={errorMessage(canvasQuery.error, "画布加载失败")}
                  onRetry={() => void canvasQuery.refetch()}
                />
              </div>
            ) : canvasQuery.data ? (
              <CanvasBoard
                data={canvasQuery.data}
                activeShotId={activeShotId}
                onSelectShot={selectShotOnCanvas}
                focusShotId={canvasFocusShot}
                library={libraryQuery.data ?? []}
                queuedShotIds={[...selectedIds]}
                onToggleQueueShot={toggleSelect}
                queueSlot={
                  <GenerationQueue
                    jobs={jobs}
                    shotOrderById={shotOrderById}
                    cancelingIds={cancelingJobs}
                    onCancel={(jobId) => void handleCancelJob(jobId)}
                    error={queueError}
                    shots={canvasQueueShots}
                    selectedShotIds={[...selectedIds]}
                    onSelectionChange={(ids) => setSelectedIds(new Set(ids))}
                    onRunBatch={(payload) => void requestEstimate(payload.kind, payload.shotIds)}
                    episodeLabel={
                      view ? `第 ${view.episode.no} 话 · ${view.shots.length} 镜` : undefined
                    }
                  />
                }
                onOpenStoryboard={(shotId) => {
                  selectShotOnCanvas(shotId);
                  changeView("storyboard");
                }}
                onOpenLibrary={({ kind, id }) => {
                  setLibraryFocus({ kind, id });
                  setLibraryOpen(true);
                }}
                onOpenFrames={(shotId) => {
                  setFrameShotId(shotId);
                  setFramesOpen(true);
                }}
                onOpenShortcuts={() => setShortcutsOpen(true)}
                onOpenClip={(shotId) => {
                  // 成片用首尾帧弹层看（里面有「通过/重抽」，也看得到 clip 状态）
                  setFrameShotId(shotId);
                  setFramesOpen(true);
                }}
              />
            ) : canvasQuery.isFetching ? (
              <div className="dp-view-state">
                <LoadingState label="正在加载画布…" />
              </div>
            ) : (
              <div className="dp-view-state">
                <EmptyState
                  title="画布没有加载出来"
                  description="请求没有返回画布数据。重试一次；故事板与时间线不受影响。"
                  action={
                    <button
                      type="button"
                      className="dp-btn"
                      onClick={() => void canvasQuery.refetch()}
                    >
                      重试
                    </button>
                  }
                />
              </div>
            )}
          </section>
        ) : viewMode === "timeline" ? (
          <section className="dp-viewport dp-viewport-view" aria-label="时间线">
            {timelineQuery.isError ? (
              <div className="dp-view-state">
                <ErrorState
                  title="时间线加载失败"
                  message={errorMessage(timelineQuery.error, "时间线加载失败")}
                  onRetry={() => void timelineQuery.refetch()}
                />
              </div>
            ) : timelineQuery.data ? (
              <TimelineView
                data={timelineQuery.data}
                shots={view?.shots}
                activeShotId={activeShotId}
                onSelectShot={focusShotFromView}
                onOpenStoryboard={openStoryboardAtShot}
              />
            ) : timelineQuery.isFetching ? (
              <div className="dp-view-state">
                <LoadingState label="正在加载时间线…" />
              </div>
            ) : (
              <div className="dp-view-state">
                <EmptyState
                  title="时间线没有加载出来"
                  description="请求没有返回时间线数据。重试一次；故事板与画布不受影响。"
                  action={
                    <button
                      type="button"
                      className="dp-btn"
                      onClick={() => void timelineQuery.refetch()}
                    >
                      重试
                    </button>
                  }
                />
              </div>
            )}
          </section>
        ) : viewMode === "library" ? (
          <section className="dp-viewport dp-viewport-view dp-lib-view" aria-label="资料库视图">
            <LibraryDialog
              variant="inline"
              open
              projectId={projectId}
              onClose={closeLibrary}
              focus={libraryFocus}
              tab={libraryTab}
              onTabChange={setLibraryTab}
              episodes={project?.episodes ?? []}
              episodeId={activeEpisodeId ?? null}
              project={project ?? null}
              storyboard={view}
              onOpenStoryboard={openStoryboardAtScene}
              onGenerate={focusAgentInput}
              onNotify={push}
            />
          </section>
        ) : viewMode === "outline" ? (
          <section className="dp-viewport dp-viewport-view" aria-label="大纲">
            <OutlineView
              projectId={projectId}
              project={project ?? null}
              storyboard={view}
              onOpenScript={(episodeId) => {
                if (episodeId && episodeId !== activeEpisodeId) selectEpisode(episodeId);
                changeView("script");
              }}
              onOpenStoryboard={() => changeView("storyboard")}
              onOpenOutput={openOutputHint}
              onGenerate={focusAgentInput}
            />
          </section>
        ) : viewMode === "script" ? (
          <section className="dp-viewport dp-viewport-view" aria-label="剧本">
            <ScriptView
              projectId={projectId}
              project={project ?? null}
              storyboard={view}
              onOpenStoryboard={({ sceneId }) => openStoryboardAtScene(sceneId)}
              onOpenOutput={openOutputHint}
              onGenerate={focusAgentInput}
            />
          </section>
        ) : (
          <section className="dp-viewport dp-viewport-view" aria-label="数据视图">
            <DataView view={view} canvas={canvasQuery.data ?? null} activeShot={activeShot} />
          </section>
        )}
      </main>

      {narrow && inspectorOpen ? (
        <div
          className="dp-drawer-scrim"
          data-testid="inspector-scrim"
          aria-hidden="true"
          onClick={closeInspector}
        />
      ) : null}

      <Inspector
        projectId={projectId}
        events={hub}
        streamStatus={streamStatus}
        activeShot={activeShot}
        activeScene={activeScene}
        quickPrompts={quickPrompts}
        open={inspectorOpen}
        panelRef={inspectorRef}
        onBusyChange={setAgentBusy}
        onClose={closeInspector}
        onConfirmShot={confirmShot}
        onRegenerateShot={regenerateShot}
        currentRev={view?.rev ?? null}
        activeTab={inspectorTab}
        onOpenCanvas={openCanvasAtShot}
        onReverted={handleReverted}
        onTabChange={(tab) => {
          setInspectorTab(tab);
          // 打开历史时对齐一次：自己刚提交过的 rev 也要立刻出现。
          if (tab === "history") {
            void queryClient.invalidateQueries({ queryKey: ["history", projectId] });
          }
        }}
      />

      {pendingGeneration ? (
        <EstimateDialog
          pending={pendingGeneration}
          balance={meQuery.data?.miya.usablePoints ?? null}
          submitting={submittingGeneration}
          onConfirm={() => void confirmGeneration()}
          onClose={closeEstimate}
        />
      ) : null}

      <AccountMenu
        open={accountOpen}
        account={summarizeAccount(meQuery.data)}
        loading={meQuery.isPending}
        error={meQuery.isError ? errorMessage(meQuery.error, "账号加载失败") : null}
        onClose={() => setAccountOpen(false)}
        onOpenUsage={() => {
          setAccountOpen(false);
          setUsageOpen(true);
        }}
        onLogout={() => void handleLogout()}
        loggingOut={loggingOut}
      />

      <UsageDialog
        open={usageOpen}
        onClose={() => setUsageOpen(false)}
        purchaseUrl={meQuery.data?.miya.purchaseUrl ?? null}
        onCleanMedia={() =>
          push("info", "清理媒体：在项目里删掉不再需要的素材即可释放配额（批量清理在 M5）")
        }
      />

      <ShortcutsSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <FrameConfirmDialog
        open={framesOpen}
        shot={view?.shots.find((item) => item.id === frameShotId) ?? null}
        shotLabel={frameShotLabel}
        onClose={() => setFramesOpen(false)}
        onApprove={(shotId) => {
          setFramesOpen(false);
          confirmShot(shotId);
        }}
        onRegenerate={(shotId) => {
          setFramesOpen(false);
          regenerateShot(shotId);
        }}
      />

      <LibraryDialog
        projectId={projectId}
        open={libraryOpen}
        onClose={closeLibrary}
        focus={libraryFocus}
        episodes={project?.episodes ?? []}
        episodeId={activeEpisodeId ?? null}
        project={project ?? null}
        storyboard={view}
        onOpenStoryboard={openStoryboardAtScene}
        onGenerate={focusAgentInput}
        onNotify={push}
      />

      <ShareDialog
        projectId={projectId}
        open={shareOpen}
        onClose={closeShare}
        episodeId={activeEpisodeId ?? null}
        episodes={project?.episodes ?? []}
        onNotify={push}
      />

      <OutputDialog
        open={outputDialog !== null}
        mode={outputDialog?.mode ?? "create"}
        output={outputDialog?.output ?? null}
        busy={outputBusy}
        error={outputError}
        onClose={closeOutputDialog}
        onCreate={(input) => void handleCreateOutput(input)}
        onUpdate={(input) => void handleRenameOutput(input)}
      />

      <ExportStatus
        state={exportState}
        onClose={() => setExportState({ phase: "idle" })}
        onRetry={() => void handleExportZip()}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
